import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from './components/AdminLayout';
import { API_ENDPOINTS } from '../config/api';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentForm, setPaymentForm] = useState({ 
    show: false, 
    orderId: null, 
    amount: '', 
    currentPaid: 0, 
    totalAmount: 0,
    isAdvance: false 
  });
  const [workflowView, setWorkflowView] = useState({ 
    show: false, 
    orderId: null, 
    workflow: [],
    order: null,
    payment_summary: null
  });
  const [formData, setFormData] = useState({
    customer_id: '',
    service_id: '',
    measurement_id: '',
    total_amount: '',
    notes: ''
  });

  const statusOptions = ['Received', 'In Progress', 'Completed', 'Payment Validated', 'Delivered'];

  useEffect(() => {
    fetchOrders();
    fetchCustomers();
    fetchServices();
    fetchMeasurements();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching orders from:', API_ENDPOINTS.ADMIN.ORDERS);
      
      const response = await axios.get(API_ENDPOINTS.ADMIN.ORDERS, {
        timeout: 8000,
      });
      
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      handleApiError(error, 'fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN.CUSTOMERS, {
        timeout: 8000,
      });
      
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN.SERVICES, {
        timeout: 8000,
      });
      
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchMeasurements = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ADMIN.MEASUREMENTS, {
        timeout: 8000,
      });
      
      setMeasurements(response.data);
    } catch (error) {
      console.error('Error fetching measurements:', error);
    }
  };

  const handleApiError = (error, operation) => {
    if (error.code === 'ECONNABORTED') {
      setError(`Request timeout while trying to ${operation}. Please check your connection.`);
    } else if (error.response) {
      setError(`Server error: ${error.response.status}. Failed to ${operation}.`);
    } else if (error.request) {
      setError(`Network error. Please check if the server is running.`);
    } else {
      setError(`Unable to ${operation}. Please try again later.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await axios.post(API_ENDPOINTS.ADMIN.ORDERS, formData, {
        timeout: 8000,
      });
      
      await fetchOrders();
      setShowForm(false);
      setFormData({
        customer_id: '', service_id: '', measurement_id: '', total_amount: '', notes: ''
      });
      alert('Order created successfully!');
    } catch (error) {
      console.error('Error creating order:', error);
      handleApiError(error, 'create order');
      alert('Error creating order. Please check the error message below.');
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      setError('');
      await axios.put(`${API_ENDPOINTS.ADMIN.ORDERS}/${orderId}/status`, {
        status: newStatus
      }, {
        timeout: 8000,
      });
      
      await fetchOrders();
      alert('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating order status:', error);
      handleApiError(error, 'update order status');
      alert('Error updating status. Please check the error message below.');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      setError('');
      const paymentAmount = parseFloat(paymentForm.amount);
      
      if (paymentAmount <= 0) {
        alert('Payment amount must be greater than 0');
        return;
      }

      if (paymentAmount > (paymentForm.totalAmount - paymentForm.currentPaid)) {
        alert('Payment amount cannot exceed the remaining balance');
        return;
      }

      // Calculate the new total paid amount
      const newTotalPaid = parseFloat(paymentForm.currentPaid) + paymentAmount;
      
      await axios.put(`${API_ENDPOINTS.ADMIN.ORDERS}/${paymentForm.orderId}/payment`, {
        amount_paid: newTotalPaid
      }, {
        timeout: 8000,
      });
      
      await fetchOrders();
      setPaymentForm({ show: false, orderId: null, amount: '', currentPaid: 0, totalAmount: 0, isAdvance: false });
      
      // Show success message with details
      const balance = paymentForm.totalAmount - newTotalPaid;
      if (balance <= 0) {
        alert('✅ Payment successful! Order is now fully paid and ready for delivery.');
      } else {
        alert(`✅ Payment of ₹${paymentAmount} recorded! Balance remaining: ₹${balance}`);
      }
      
    } catch (error) {
      console.error('Error updating payment:', error);
      handleApiError(error, 'update payment');
      alert('Error updating payment. Please check the error message below.');
    }
  };

  const viewWorkflow = async (orderId) => {
    try {
      setError('');
      const response = await axios.get(`${API_ENDPOINTS.ADMIN.ORDERS}/${orderId}/workflow`, {
        timeout: 8000,
      });
      
      setWorkflowView({ show: true, orderId, ...response.data });
    } catch (error) {
      console.error('Error fetching workflow:', error);
      handleApiError(error, 'fetch workflow details');
      alert('Error fetching workflow details. Please check the error message below.');
    }
  };

  const generateInvoice = async (orderId) => {
    try {
      setError('');
      const response = await axios.get(`${API_ENDPOINTS.ADMIN.INVOICES}/${orderId}`, {
        responseType: 'blob',
        timeout: 10000,
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error generating invoice:', error);
      handleApiError(error, 'generate invoice');
      alert('Error generating invoice. Please check the error message below.');
    }
  };

  const getStatusBadge = (status, amountPaid, totalAmount) => {
    const statusColors = {
      'Received': 'bg-secondary',
      'In Progress': 'bg-primary',
      'Completed': 'bg-warning',
      'Payment Validated': 'bg-info',
      'Delivered': 'bg-success'
    };

    const balance = totalAmount - (amountPaid || 0);
    let paymentBadge = '';
    
    if (balance <= 0) {
      paymentBadge = '<span class="badge bg-success ms-1">Fully Paid</span>';
    } else if (amountPaid > 0) {
      paymentBadge = `<span class="badge bg-warning ms-1">Balance: ₹${balance}</span>`;
    } else {
      paymentBadge = `<span class="badge bg-danger ms-1">Due: ₹${balance}</span>`;
    }

    return (
      <div>
        <span className={`badge ${statusColors[status]}`}>{status}</span>
        <span dangerouslySetInnerHTML={{ __html: paymentBadge }} />
      </div>
    );
  };

  const calculateBalance = (order) => {
    return order.total_amount - (order.amount_paid || 0);
  };

  return (
    <AdminLayout>
      <div className="admin-content">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2>Order Management</h2>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            <i className="fas fa-plus me-2"></i>Create Order
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Loading State */}
        {loading && orders.length === 0 && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading orders...</p>
          </div>
        )}

        {/* Create Order Form */}
        {showForm && (
          <div className="card form-container mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Create New Order</h4>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setShowForm(false)}
                disabled={loading}
              ></button>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Customer *</label>
                      <select
                        className="form-control"
                        value={formData.customer_id}
                        onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Customer</option>
                        {customers.map(customer => (
                          <option key={customer.id} value={customer.id}>
                            {customer.name} - {customer.phone}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Service *</label>
                      <select
                        className="form-control"
                        value={formData.service_id}
                        onChange={(e) => {
                          const serviceId = e.target.value;
                          const selectedService = services.find(s => s.id == serviceId);
                          setFormData({ 
                            ...formData, 
                            service_id: serviceId,
                            total_amount: selectedService ? selectedService.price : ''
                          });
                        }}
                        required
                        disabled={loading}
                      >
                        <option value="">Select Service</option>
                        {services.map(service => (
                          <option key={service.id} value={service.id}>
                            {service.name} - ₹{service.price}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Measurement</label>
                      <select
                        className="form-control"
                        value={formData.measurement_id}
                        onChange={(e) => setFormData({ ...formData, measurement_id: e.target.value })}
                        disabled={loading}
                      >
                        <option value="">Select Measurement (Optional)</option>
                        {measurements.map(measurement => (
                          <option key={measurement.id} value={measurement.id}>
                            {measurement.customer_name} - Chest: {measurement.chest}"
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Total Amount (₹) *</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.total_amount}
                        onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                        required
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Any special instructions..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    disabled={loading}
                  ></textarea>
                </div>

                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating...
                      </>
                    ) : (
                      'Create Order'
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Payment Form */}
        {paymentForm.show && (
          <div className="card form-container mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Add Payment - Order #{paymentForm.orderId}</h4>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setPaymentForm({ show: false, orderId: null, amount: '', currentPaid: 0, totalAmount: 0, isAdvance: false })}
                disabled={loading}
              ></button>
            </div>
            <div className="card-body">
              {/* Payment Summary */}
              <div className="payment-summary mb-4 p-3 bg-light rounded">
                <h5>Payment Summary</h5>
                <div className="row text-center">
                  <div className="col-md-4">
                    <div className="border-end">
                      <strong className="d-block">Total Amount</strong>
                      <span className="h5 text-primary">₹{paymentForm.totalAmount}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="border-end">
                      <strong className="d-block">Already Paid</strong>
                      <span className="h5 text-success">₹{paymentForm.currentPaid}</span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div>
                      <strong className="d-block">Balance Due</strong>
                      <span className="h5 text-danger">₹{paymentForm.totalAmount - paymentForm.currentPaid}</span>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePayment}>
                <div className="mb-3">
                  <label className="form-label">
                    <strong>Payment Amount (₹) *</strong>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={paymentForm.totalAmount - paymentForm.currentPaid}
                    className="form-control form-control-lg"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    required
                    disabled={loading}
                  />
                  <div className="form-text">
                    Maximum allowed: ₹{paymentForm.totalAmount - paymentForm.currentPaid}
                  </div>
                </div>

                {/* Quick Payment Buttons */}
                <div className="mb-3">
                  <label className="form-label"><strong>Quick Payment Options:</strong></label>
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm quick-payment-btn"
                      onClick={() => {
                        const balance = paymentForm.totalAmount - paymentForm.currentPaid;
                        setPaymentForm({ ...paymentForm, amount: balance });
                      }}
                      disabled={loading}
                    >
                      Pay Full Balance (₹{paymentForm.totalAmount - paymentForm.currentPaid})
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm quick-payment-btn"
                      onClick={() => {
                        const halfBalance = (paymentForm.totalAmount - paymentForm.currentPaid) / 2;
                        setPaymentForm({ ...paymentForm, amount: Math.round(halfBalance) });
                      }}
                      disabled={loading}
                    >
                      Pay Half Balance
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-info btn-sm quick-payment-btn"
                      onClick={() => setPaymentForm({ ...paymentForm, amount: 500 })}
                      disabled={loading}
                    >
                      ₹500
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-info btn-sm quick-payment-btn"
                      onClick={() => setPaymentForm({ ...paymentForm, amount: 1000 })}
                      disabled={loading}
                    >
                      ₹1000
                    </button>
                  </div>
                </div>

                {/* Payment Type */}
                <div className="mb-3">
                  <label className="form-label"><strong>Payment Type</strong></label>
                  <div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="paymentType"
                        id="advancePayment"
                        checked={paymentForm.isAdvance}
                        onChange={() => setPaymentForm({ ...paymentForm, isAdvance: true })}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="advancePayment">
                        Advance Payment
                      </label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="paymentType"
                        id="balancePayment"
                        checked={!paymentForm.isAdvance}
                        onChange={() => setPaymentForm({ ...paymentForm, isAdvance: false })}
                        disabled={loading}
                      />
                      <label className="form-check-label" htmlFor="balancePayment">
                        Balance Payment
                      </label>
                    </div>
                  </div>
                </div>

                {/* Payment Details After Calculation */}
                {paymentForm.amount > 0 && (
                  <div className="payment-preview mb-3 p-3 bg-info text-white rounded">
                    <h6>Payment Preview:</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Current Payment:</strong> ₹{paymentForm.amount}
                      </div>
                      <div className="col-md-6">
                        <strong>New Total Paid:</strong> ₹{parseFloat(paymentForm.currentPaid) + parseFloat(paymentForm.amount)}
                      </div>
                      <div className="col-md-6">
                        <strong>Remaining Balance:</strong> ₹{paymentForm.totalAmount - (parseFloat(paymentForm.currentPaid) + parseFloat(paymentForm.amount))}
                      </div>
                      <div className="col-md-6">
                        <strong>Status After Payment:</strong> 
                        {(parseFloat(paymentForm.currentPaid) + parseFloat(paymentForm.amount)) >= paymentForm.totalAmount 
                          ? 'Fully Paid' 
                          : 'Partially Paid'}
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-2">
                  <button 
                    type="submit" 
                    className="btn btn-success btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check me-2"></i>
                        Confirm Payment of ₹{paymentForm.amount}
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setPaymentForm({ show: false, orderId: null, amount: '', currentPaid: 0, totalAmount: 0, isAdvance: false })}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Workflow View */}
        {workflowView.show && (
          <div className="card form-container mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Order Workflow - #{workflowView.orderId}</h4>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setWorkflowView({ show: false, orderId: null, workflow: [], order: null, payment_summary: null })}
              ></button>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <h5>Customer: {workflowView.order?.customer_name}</h5>
                <h6>Service: {workflowView.order?.service_name}</h6>
                <h6>Total Amount: ₹{workflowView.order?.total_amount}</h6>
              </div>

              {/* Workflow Steps */}
              <div className="workflow-steps">
                {workflowView.workflow?.map((step, index) => (
                  <div key={index} className={`workflow-step ${step.status}`}>
                    <div className="step-indicator">
                      <i className={`fas ${
                        step.status === 'completed' ? 'fa-check-circle text-success' : 
                        step.status === 'pending' ? 'fa-clock text-secondary' : 'fa-times-circle text-danger'
                      }`}></i>
                    </div>
                    <div className="step-content">
                      <h6>{step.step}</h6>
                      <p className="text-muted">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Payment Summary */}
              {workflowView.payment_summary && (
                <div className="payment-summary mt-4 p-3 bg-light rounded">
                  <h5>Payment Summary</h5>
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Total Amount:</strong> ₹{workflowView.payment_summary.total_amount}
                    </div>
                    <div className="col-md-4">
                      <strong>Amount Paid:</strong> ₹{workflowView.payment_summary.amount_paid}
                    </div>
                    <div className="col-md-4">
                      <strong>Balance:</strong> 
                      <span className={workflowView.payment_summary.balance > 0 ? 'text-danger fw-bold' : 'text-success'}>
                        ₹{workflowView.payment_summary.balance}
                      </span>
                    </div>
                  </div>
                  <div className="progress mt-2" style={{height: '10px'}}>
                    <div 
                      className={`progress-bar ${workflowView.payment_summary.is_fully_paid ? 'bg-success' : 'bg-warning'}`}
                      style={{width: `${workflowView.payment_summary.payment_percentage}%`}}
                    ></div>
                  </div>
                  <small className="text-muted">
                    {workflowView.payment_summary.payment_percentage.toFixed(1)}% Paid
                    {!workflowView.payment_summary.is_fully_paid && ' - Full payment required before delivery'}
                  </small>
                </div>
              )}

              <div className="d-flex gap-2 mt-3">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setWorkflowView({ show: false, orderId: null, workflow: [], order: null, payment_summary: null })}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Orders Table */}
        {orders.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Orders List ({orders.length})</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Total Amount</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status & Payment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => {
                      const balance = calculateBalance(order);
                      return (
                        <tr key={order.id}>
                          <td><strong>#{order.id}</strong></td>
                          <td>{order.customer_name}</td>
                          <td>{order.service_name}</td>
                          <td>₹{order.total_amount}</td>
                          <td>₹{order.amount_paid || 0}</td>
                          <td className={balance > 0 ? 'text-danger fw-bold' : 'text-success'}>
                            ₹{balance}
                          </td>
                          <td>
                            {getStatusBadge(order.status, order.amount_paid || 0, order.total_amount)}
                          </td>
                          <td>
                            <div className="btn-group btn-group-sm">
                              <select 
                                className="form-select form-select-sm"
                                value={order.status}
                                onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                                disabled={loading}
                              >
                                {statusOptions.map(status => (
                                  <option key={status} value={status}>{status}</option>
                                ))}
                              </select>
                              <button 
                                className="btn btn-info"
                                onClick={() => {
                                  const balance = order.total_amount - (order.amount_paid || 0);
                                  setPaymentForm({ 
                                    show: true, 
                                    orderId: order.id, 
                                    amount: balance > 0 ? balance : 0,
                                    currentPaid: order.amount_paid || 0,
                                    totalAmount: order.total_amount,
                                    isAdvance: false
                                  });
                                }}
                                disabled={loading}
                                title="Add Payment"
                              >
                                <i className="fas fa-money-bill"></i>
                              </button>
                              <button 
                                className="btn btn-primary"
                                onClick={() => viewWorkflow(order.id)}
                                disabled={loading}
                                title="View Workflow"
                              >
                                <i className="fas fa-project-diagram"></i>
                              </button>
                              <button 
                                className="btn btn-success"
                                onClick={() => generateInvoice(order.id)}
                                disabled={loading}
                                title="Generate Invoice"
                              >
                                <i className="fas fa-file-pdf"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .workflow-step {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
          padding: 1rem;
          border-radius: 0.5rem;
          background: #f8f9fa;
        }
        .workflow-step.completed {
          background: #d1e7dd;
        }
        .step-indicator {
          margin-right: 1rem;
          font-size: 1.5rem;
        }
        .step-content h6 {
          margin-bottom: 0.25rem;
        }
        .quick-payment-btn {
          white-space: nowrap;
        }
      `}</style>
    </AdminLayout>
  );
};

export default OrderManagement;